// DERIVADO DE: gestion-web-cliente@0f7bf5b12
//   src/app/auxiliares/servicios/{servicios-tecnicos-pendientes,materiales-pendientes}.service.ts
// CAMBIOS: signals + `resource()` en vez de `Lista2Service`; `onlyTotalCount` en
//   vez de `limit: 1`; `includeChildren` sólo con clientes hijos; sin websocket
//   (FARO no tiene): recuento por sondeo y al navegar por Mantenimiento.

import { DestroyRef, Service, computed, inject, resource } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { JerarquiaClientesStore } from '@compartido/clientes/jerarquia-clientes.store';
import { PermisosService } from '@core/permisos/permisos.service';
import { SesionStore } from '@core/sesion/sesion.store';

import { MaterialesHttp, SolicitudesHttp } from './mantenimiento.http';

/**
 * Cada cuánto se recuenta. Es la cadencia de IRIX cuando no tiene websocket;
 * FARO no lo tiene nunca.
 */
const CADENCIA_SEG = 30;

/**
 * Cuántas solicitudes de servicio técnico y cuántos pedidos de materiales
 * esperan a alguien. Son los badges de la sección Mantenimiento del menú.
 *
 * **Mismo criterio que IRIX y que la solapa "Abiertas" del listado**: las que
 * requieren atención (no finalizadas), de Servicio Técnico o Verificación, sin
 * contar las propias programadas para más adelante. Como el listado de FARO,
 * sólo las de luminarias. Los materiales son los pedidos pendientes de
 * aprobación.
 *
 * Como en IRIX: sin sonido ni notificación, y el número no se pone en cero al
 * entrar a la vista; baja cuando las solicitudes se atienden.
 *
 * Sin permiso para la vista no se cuenta: el ítem ni está en el menú, y en IRIX
 * fue un problema que todos los usuarios quedaran consultando un badge que no
 * iban a ver.
 */
@Service()
export class PendientesMantenimientoStore {
  private readonly solicitudesHttp = inject(SolicitudesHttp);
  private readonly materialesHttp = inject(MaterialesHttp);
  private readonly permisos = inject(PermisosService);
  private readonly sesion = inject(SesionStore);
  private readonly jerarquia = inject(JerarquiaClientesStore);

  constructor() {
    const intervalo = setInterval(() => {
      if (document.visibilityState === 'visible') this.recontar();
    }, CADENCIA_SEG * 1000);
    const destroyRef = inject(DestroyRef);
    destroyRef.onDestroy(() => clearInterval(intervalo));

    // IRIX se entera por websocket de que se creó, asignó o cerró una solicitud.
    // Sin websocket, lo más cerca es recontar al entrar o salir de
    // Mantenimiento: es donde se crean y se cierran.
    let anterior = '';
    const sub = inject(Router)
      .events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const url = e.urlAfterRedirects;
        if (url.startsWith('/mantenimiento') || anterior.startsWith('/mantenimiento')) {
          this.recontar();
        }
        anterior = url;
      });
    destroyRef.onDestroy(() => sub.unsubscribe());
  }

  /**
   * Vuelve a contar ya, sin esperar el próximo sondeo.
   *
   * Con `reload()` y no con un contador en `params`: cambiar los params deja el
   * valor en `undefined` mientras carga, y el badge parpadearía a cero en cada
   * recuento.
   */
  recontar(): void {
    this.conteoSolicitudes.reload();
    this.conteoMateriales.reload();
  }

  private readonly conteoSolicitudes = resource({
    // Con hijos se cuentan también los suyos; sin hijos, `includeChildren` sólo
    // haría lenta la consulta (ver `JerarquiaClientesStore.tieneHijos`).
    params: () => {
      const hijos = this.jerarquia.tieneHijos();
      if (!this.permisos.puedeVer('solicitudes') || hijos === undefined) return undefined;
      return {
        hijos,
        idPermiso: this.sesion.idPermiso(),
        idUsuario: this.sesion.usuario()?._id,
      };
    },
    loader: ({ params }) =>
      this.solicitudesHttp.listar(
        {
          filter: JSON.stringify(filtroPendientes(params.idUsuario, new Date().toISOString())),
          onlyTotalCount: true,
          includeChildren: params.hijos || undefined,
        },
        // Recuento de fondo para el badge del menú: si una vuelta falla, la
        // próxima lo corrige. Sin esto salía un aviso de error cada 30 s.
        { silencioso: true },
      ),
  });

  private readonly conteoMateriales = resource({
    params: () => {
      const hijos = this.jerarquia.tieneHijos();
      if (!this.permisos.puedeVer('materiales') || hijos === undefined) return undefined;
      return { hijos, idPermiso: this.sesion.idPermiso() };
    },
    loader: ({ params }) =>
      this.materialesHttp.listar(
        {
          filter: JSON.stringify({ tipo: 'pedido', estado: 'Pendiente' }),
          onlyTotalCount: true,
          includeChildren: params.hijos || undefined,
        },
        { silencioso: true },
      ),
  });

  /** `0` también cuando falla: un badge que miente para arriba es peor que ninguno. */
  readonly solicitudes = computed(() =>
    this.conteoSolicitudes.hasValue() ? (this.conteoSolicitudes.value()?.totalCount ?? 0) : 0,
  );

  readonly materiales = computed(() =>
    this.conteoMateriales.hasValue() ? (this.conteoMateriales.value()?.totalCount ?? 0) : 0,
  );
}

/**
 * Las solicitudes abiertas, sin filtros del usuario.
 *
 * Es el mismo filtro que `filtroMongo({ historico: false }, …)` del listado
 * (`dominio/mantenimiento/filtros-solicitudes.ts`), que no se importa porque
 * `compartido/` no depende de un dominio. Si cambia uno, cambia el otro.
 */
function filtroPendientes(idUsuario: string | undefined, ahora: string): Record<string, unknown> {
  const filtro: Record<string, unknown> = {
    tipoEvento: 'Evento Técnico Luminaria',
    filtrador: { $in: ['Servicio Técnico', 'Verificación'] },
    requiereAtencion: true,
  };
  if (idUsuario) {
    filtro['$and'] = [
      {
        $or: [
          { 'detallesTecnicos.idTecnicoAsignado': { $ne: idUsuario } },
          { 'detallesTecnicos.fechaDisponibleParaTratar': { $exists: false } },
          { 'detallesTecnicos.fechaDisponibleParaTratar': null },
          { 'detallesTecnicos.fechaDisponibleParaTratar': { $lte: ahora } },
        ],
      },
    ];
  }
  return filtro;
}
